import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import FileUploader from '../components/dc/FileUploader'
import { dcApi } from '../services/dc.api'
import { Distributor, UploadAndProcessResponse } from '../types/dc.types'

type Phase = 'upload' | 'processing' | 'select-distributor' | 'results'

export default function DCUploadPage() {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('upload')
  const [defaultDiscount, setDefaultDiscount] = useState<number>(8)
  const [discountOfferMode, setDiscountOfferMode] = useState<string>('combine')
  const [error, setError] = useState<string | null>(null)
  const [stageText, setStageText] = useState('')
  const [result, setResult] = useState<UploadAndProcessResponse | null>(null)

  // For distributor selection flow
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null)

  const { data: distributors = [] } = useQuery({
    queryKey: ['distributors'],
    queryFn: dcApi.getDistributors
  })

  // Main upload & process mutation
  const processMutation = useMutation({
    mutationFn: (file: File) => {
      setStageText('Uploading and parsing file...')
      return dcApi.uploadAndProcess(file, defaultDiscount, discountOfferMode)
    },
    onSuccess: (data) => {
      if (data.needsDistributor) {
        setResult(data)
        setPhase('select-distributor')
      } else {
        setResult(data)
        setPhase('results')
      }
    },
    onError: (err: Error) => {
      setError(err.message)
      setPhase('upload')
    }
  })

  // Continue processing after distributor selection
  const continueMutation = useMutation({
    mutationFn: () => {
      if (!result || !selectedDistributor) throw new Error('Missing data')
      setStageText('Matching items and calculating prices...')
      return dcApi.continueProcessing({
        uploadId: result.uploadId,
        distributorId: selectedDistributor.id,
        distributorName: selectedDistributor.distributor_name,
        defaultDiscountPct: defaultDiscount,
        discountOfferMode
      })
    },
    onSuccess: (data) => {
      setResult(data)
      setPhase('results')
    },
    onError: (err: Error) => {
      setError(err.message)
    }
  })

  const handleFileUpload = useCallback((file: File) => {
    setError(null)
    setPhase('processing')
    processMutation.mutate(file)
  }, [processMutation, defaultDiscount])

  const handleReset = () => {
    setPhase('upload')
    setResult(null)
    setError(null)
    setStageText('')
    setSelectedDistributor(null)
    setDiscountOfferMode('combine')
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Upload Price List
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Phase 1: Upload */}
      {phase === 'upload' && (
        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            <FileUploader
              onUpload={handleFileUpload}
              isLoading={false}
              acceptedTypes={['.pdf', '.csv', '.xlsx', '.xls']}
            />
            <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
              <TextField
                type="number"
                label="Default Discount %"
                value={defaultDiscount}
                onChange={(e) => setDefaultDiscount(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                size="small"
                helperText="Applied when item has no specific discount"
                sx={{ width: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Discount + Offer</InputLabel>
                <Select
                  value={discountOfferMode}
                  label="Discount + Offer"
                  onChange={(e) => setDiscountOfferMode(e.target.value)}
                >
                  <MenuItem value="combine">Combine (stack both)</MenuItem>
                  <MenuItem value="replace">Replace (offer overrides discount)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Phase 2: Processing */}
      {phase === 'processing' && (
        <Paper sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={4}>
            <CircularProgress size={48} />
            <Typography variant="h6">{stageText || 'Processing...'}</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take up to a minute for large files with AI parsing.
            </Typography>
            <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
          </Box>
        </Paper>
      )}

      {/* Phase 2b: Distributor Selection */}
      {phase === 'select-distributor' && result && (
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Distributor Not Detected
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Could not auto-detect the distributor
            {result.detectedDistributorName && ` (best guess: "${result.detectedDistributorName}")`}.
            {' '}Found {result.totalItems} items. Please select the distributor to continue.
          </Alert>

          <Stack spacing={3} sx={{ maxWidth: 500 }}>
            <Autocomplete
              options={distributors}
              getOptionLabel={(option: Distributor) => option.distributor_name}
              value={selectedDistributor}
              onChange={(_, value) => setSelectedDistributor(value)}
              renderInput={(params) => (
                <TextField {...params} label="Select Distributor" />
              )}
            />

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={() => {
                  setPhase('processing')
                  continueMutation.mutate()
                }}
                disabled={!selectedDistributor || continueMutation.isPending}
                startIcon={continueMutation.isPending ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              >
                {continueMutation.isPending ? 'Processing...' : 'Continue Processing'}
              </Button>
              <Button variant="outlined" onClick={handleReset}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Phase 3: Results */}
      {phase === 'results' && result && (
        <Stack spacing={3}>
          {/* Summary Card */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6">Upload Complete</Typography>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2}>
                <Chip label={`Distributor: ${result.distributorName}`} variant="outlined" />
                <Chip label={`Total Items: ${result.totalItems}`} color="primary" />
                <Chip label={`Matched: ${result.matchedItems}`} color="success" />
                {(result.unmatchedItems ?? 0) > 0 && (
                  <Chip label={`Unmatched: ${result.unmatchedItems}`} color="warning" />
                )}
                <Chip label={`Saved: ${result.savedItems}`} color="info" />
                <Chip label={`Parser: ${result.parserType}`} variant="outlined" size="small" />
                {result.processingTimeMs && (
                  <Chip
                    label={`${(result.processingTimeMs / 1000).toFixed(1)}s`}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Items Preview Table */}
          {result.items && result.items.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Preview (first {Math.min(result.items.length, 50)} items)
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Matched Medicine</TableCell>
                      <TableCell align="right">PTR</TableCell>
                      <TableCell align="right">Discount %</TableCell>
                      <TableCell align="right">Landing Cost</TableCell>
                      <TableCell>Match</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.items.slice(0, 50).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.raw_product_name}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.matched_medicine_name || '-'}
                        </TableCell>
                        <TableCell align="right">
                          {item.ptr != null ? `${Number(item.ptr).toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {item.discount_percentage ? `${item.discount_percentage}%` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {item.landing_cost != null ? `${Number(item.landing_cost).toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>
                          {item.is_matched ? (
                            <Chip label={`${Math.round((item.match_confidence || 0) * 100)}%`} size="small" color="success" />
                          ) : (
                            <Chip label="No" size="small" color="default" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Action Buttons */}
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate('/comparison')}
            >
              View Comparison
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              Upload Another
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}
